import { useEffect, useMemo, useState } from "react";
import { AppointmentActivity } from "appointment-experience-client";
import { fetchAppointmentActivities } from "../../api-helpers/AppointmentApiHelper";
import { ACTIVITY_TYPE_CONFIG } from "../../constants/AppointmentActivity";
import { formatTime } from "../../helper/dateFormatter";
import "../../assets/scss/custom/_appointmentDetail.scss";
import {
  ActivityRow,
  ActivityRowItem,
  activityRowItem,
} from "./appointment-detail/ActivityRow";

type AppointmentActivityPanelProps = {
  appointmentId: string;
  /** Called once the activity has loaded or failed to. */
  onSettled?: () => void;
};

const KNOWN_TYPE_ORDER = Object.keys(ACTIVITY_TYPE_CONFIG);

/** Groups the activities by category, known categories first in a fixed order. */
const groupByType = (activities: AppointmentActivity[]) => {
  const groups = new Map<string, AppointmentActivity[]>();
  activities.forEach((activity) => {
    const group = groups.get(activity.activityType) ?? [];
    group.push(activity);
    groups.set(activity.activityType, group);
  });

  const rank = (type: string) => {
    const index = KNOWN_TYPE_ORDER.indexOf(type);
    return index === -1 ? KNOWN_TYPE_ORDER.length : index;
  };
  return Array.from(groups.entries()).sort(([a], [b]) => rank(a) - rank(b));
};

export const AppointmentActivityPanel = ({
  appointmentId,
  onSettled,
}: AppointmentActivityPanelProps) => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [activities, setActivities] = useState<AppointmentActivity[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);

    fetchAppointmentActivities(appointmentId)
      .then((rows) => {
        if (!cancelled) setActivities(rows);
      })
      .catch((error) => {
        console.error("Error loading the appointment's activity:", error);
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          onSettled?.();
        }
      });

    return () => {
      cancelled = true;
    };
    // onSettled is a notification only; a new callback shouldn't refetch.
  }, [appointmentId]);

  // Known categories first, in a fixed order; the type icon marks each row.
  const items = useMemo(
    () =>
      groupByType(activities).flatMap(([, rows]) =>
        rows.map((activity): ActivityRowItem => ({
          ...activityRowItem(activity),
          meta: formatTime(activity.completedAt ?? activity.lastActivityAt),
        }))
      ),
    [activities]
  );

  const renderBody = () => {
    if (loading) {
      return (
        <span className="placeholder-wave d-block">
          <span className="placeholder col-8 mb-2"></span>
          <span className="placeholder col-6"></span>
        </span>
      );
    }
    if (failed) {
      return (
        <span className="appointment-card__empty">
          Unable to load the appointment activity
        </span>
      );
    }
    if (activities.length === 0) {
      return (
        <span className="appointment-card__empty">
          No activity recorded during this appointment
        </span>
      );
    }
    return (
      <ul className="activity-list">
        {items.map((item) => (
          <ActivityRow item={item} key={item.key} />
        ))}
      </ul>
    );
  };

  return <div className="appointment-activity-panel">{renderBody()}</div>;
};
